import { useState } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import ConfirmDialog from 'app/KinerjaKSPage/components/PanelProfil/ConfirmDialog'
import EditDialog from 'app/KinerjaKSPage/components/PanelProfil/EditDialog'
import { SKP_QUERY_KEY, useAPIUpsertSKP } from 'api/kinerja/useAPIKinerjaKS'
import {
  isValidPangkat,
  checkPangkatAndJenjang,
  getPangkatGolongan,
} from 'app/KinerjaShared/utils/pangkat'
import { isValidJenjang, mapJenjangName } from 'app/KinerjaShared/utils/jenjang'
import useSchoolContext from 'app/KinerjaKSPage/hooks/useSchoolContext'
import { queryKeyWithNpsn } from 'api/kinerja/kepala-sekolah/config'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'
import useToggleSdkContext from 'utils/useToogleSdk'

import type { HTMLAttributes } from 'react'
import type { Jenjang, KinerjaProfil } from 'types/kinerja'

interface PanelProfilProps {
  profile: KinerjaProfil
  skipEditConfirmation?: boolean
}

interface PanelProfileListItemProps extends HTMLAttributes<HTMLDivElement> {
  label: string
  value: string
  testId?: string
}

export const WARNING_TEXT_PNS_PPPK = `Pastikan Jenjang Jabatan sesuai karena akan memengaruhi Perencanaan Kinerja. Saat ini data hanya bisa diedit satu kali saja, ya.`
export const WARNING_TEXT_NON_PNS_PPPK = `Silakan hubungi operator sekolah untuk pembaruan data diri Anda di Dapodik.`

interface ActionButtonProps {
  skipEditConfirmation: boolean
  isIncomplete: boolean
  isGuruStatusUndefined: boolean
  onConfirm: () => void
  onOpenEditDialog: (open: boolean) => void
  onOpenConfirmationDialog: (open: boolean) => void
}

function ActionButton({
  skipEditConfirmation,
  isIncomplete,
  isGuruStatusUndefined,
  onConfirm,
  onOpenEditDialog,
  onOpenConfirmationDialog,
}: ActionButtonProps) {
  if (skipEditConfirmation) {
    return (
      <Button size="lg" onClick={onConfirm}>
        Data sudah sesuai
      </Button>
    )
  }

  if (isIncomplete && !isGuruStatusUndefined) {
    return (
      <Button size="lg" color="black" onClick={() => onOpenEditDialog(true)}>
        Lengkapi data diri
      </Button>
    )
  }

  return (
    <>
      {!isGuruStatusUndefined && (
        <Button size="lg" color="white" onClick={() => onOpenEditDialog(true)}>
          Edit
        </Button>
      )}
      <Button size="lg" onClick={() => onOpenConfirmationDialog(true)}>
        Data sudah sesuai
      </Button>
    </>
  )
}

function PanelListItem({
  label,
  value,
  testId,
  className,
  ...props
}: PanelProfileListItemProps) {
  return (
    <div className={clsx('flex gap-2', className)} {...props}>
      <Text variant="body" className="mb-1 w-48">
        {label}
      </Text>
      <Text variant="body-bold">:</Text>
      <Text data-testid={testId} variant="body-bold">
        {value}
      </Text>
    </div>
  )
}

export default function PanelProfil({
  profile,
  skipEditConfirmation = false,
}: PanelProfilProps) {
  const queryClient = useQueryClient()
  const toggles = useToggleSdkContext()

  const isSPNFEnabled = toggles?.['kinerja-spnf-enabled'] === true

  // create a boolean state to open dialog edit profile
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [currentProfile, setCurrentProfile] = useState(profile)
  const [isModified, setIsModified] = useState(false)

  const schoolCtx = useSchoolContext()
  const baseUrl = usePengelolaanKinerjaBaseUrl()

  const { mutateAsync: createNewSKP, isLoading } = useAPIUpsertSKP()
  const router = useRouter()
  const toast = useToast()

  // If the initial profile is not valid, (i.e. wrong pangkat or pangkat and jenjang does not match), we ask the user to modify it.
  // But we won't validate it again after the user modified it.

  const pangkatGolongan =
    isModified || isValidPangkat(currentProfile.pangkatGolongan)
      ? currentProfile.pangkatGolongan
      : '-'

  let jenjang = '-'

  if (isModified) {
    jenjang = currentProfile.jenjang
  } else if (currentProfile.kategoriPegawai === 'PNS') {
    jenjang = checkPangkatAndJenjang(
      currentProfile.pangkatGolongan,
      currentProfile.jenjang.toLowerCase() as Jenjang
    )
      ? currentProfile.jenjang
      : '-'
  } else if (currentProfile.kategoriPegawai === 'PPPK') {
    jenjang = isValidJenjang(currentProfile.jenjang)
      ? currentProfile.jenjang
      : '-'
  }

  let isIncomplete = false

  if (currentProfile.kategoriPegawai === 'PNS') {
    isIncomplete = pangkatGolongan === '-' || jenjang === '-'
  } else if (currentProfile.kategoriPegawai === 'PPPK') {
    isIncomplete = jenjang === '-'
  }

  const isGuruStatusUndefined = currentProfile.kategoriPegawai === 'UNDEFINED'

  const handleSave = (profile: KinerjaProfil) => {
    setIsModified(true)
    setCurrentProfile(profile)
  }

  const handleConfirm = async () => {
    try {
      await createNewSKP({
        pangkatGolongan: currentProfile.pangkatGolongan,
        jenjang: currentProfile.jenjang,
        ...(!!schoolCtx.currentNpsn ? { npsn: schoolCtx.currentNpsn } : {}),
        ...(isSPNFEnabled ? { role: schoolCtx.selectedSchool.role } : {}),
      })

      await queryClient.invalidateQueries({
        queryKey: queryKeyWithNpsn(SKP_QUERY_KEY, schoolCtx.currentNpsn),
      })

      setIsConfirmDialogOpen(false)

      router.push(
        `${baseUrl}/perencanaan${
          !!schoolCtx.currentNpsn ? `?id=${schoolCtx.currentNpsn}` : ''
        }`
      )
    } catch (error) {
      console.error(error)

      const message =
        error?.originalError?.response?.data?.error?.message ?? error.toString()

      toast({
        message: `Gagal mengubah profil: ${message}`,
      })
    }
  }

  return (
    <div className="p-9" data-testid="panel-profil">
      <div className="rounded-lg bg-white mt-9 max-w-7xl m-auto">
        <div className="p-9 border-b">
          <Text variant="display-sm" className="mb-6">
            Cek data diri Anda
          </Text>
          {!skipEditConfirmation && (
            <div className="flex p-4 gap-3 items-center rounded bg-surface-warning-subdued mb-6">
              <Icon>information_outlined</Icon>
              <Text variant="body">
                {isGuruStatusUndefined
                  ? WARNING_TEXT_NON_PNS_PPPK
                  : WARNING_TEXT_PNS_PPPK}
              </Text>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <PanelListItem
              testId="nama"
              label="Nama"
              value={currentProfile.nama}
            />
            {currentProfile.kategoriPegawai !== 'UNDEFINED' && (
              <PanelListItem
                testId="nip"
                label="NIP"
                value={currentProfile.nip || '-'}
              />
            )}

            <PanelListItem
              testId="status-kepegawaian"
              label="Status Kepegawaian"
              value={currentProfile.statusPegawai || '-'}
            />

            {currentProfile.kategoriPegawai === 'PNS' && (
              <PanelListItem
                testId="pangkat"
                label="Pangkat, Golongan Ruang"
                value={getPangkatGolongan(pangkatGolongan)}
              />
            )}

            {currentProfile.kategoriPegawai !== 'UNDEFINED' && (
              <PanelListItem
                testId="jenjang"
                label="Jenjang Jabatan"
                value={mapJenjangName(jenjang)}
              />
            )}

            <PanelListItem
              testId="satuan-pendidikan"
              label="Satuan Pendidikan Induk"
              value={schoolCtx.selectedSchoolName ?? profile.satuanPendidikan}
            />
          </div>
        </div>
        <div className="flex py-4 px-7">
          <div className="ml-auto flex gap-5">
            <ActionButton
              skipEditConfirmation={skipEditConfirmation}
              isIncomplete={isIncomplete}
              isGuruStatusUndefined={isGuruStatusUndefined}
              onConfirm={handleConfirm}
              onOpenEditDialog={setIsDialogOpen}
              onOpenConfirmationDialog={setIsConfirmDialogOpen}
            />
          </div>
        </div>
      </div>

      {!skipEditConfirmation && (
        <>
          <EditDialog
            profile={currentProfile}
            isOpen={isDialogOpen}
            setOpen={setIsDialogOpen}
            onSave={handleSave}
          />
          <ConfirmDialog
            profile={{
              ...currentProfile,
              satuanPendidikan:
                schoolCtx.selectedSchoolName ?? profile.satuanPendidikan,
            }}
            isOpen={isConfirmDialogOpen}
            onClose={() => setIsConfirmDialogOpen(false)}
            onConfirm={handleConfirm}
            disabledConfirmButton={isLoading}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  )
}
